import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import Alert from '@r1-frontend/ui-react/components/Alert';
import Button from '@r1-frontend/ui-react/components/buttons/button';
import { Paragraph4 } from '@r1-frontend/ui-react/components/typography/paragraph';
import { RubleSymbol } from '@r1-frontend/ui-react/experimental/purchase/cost/Cost/RubleSymbol';

import OpenChat from '~/src/components/OpenChatLink';
import { useCardPayment } from '~/src/features/payments-abn/components/CardPayment/useCardPayment';
import InfoHint from '~/src/features/payments-abn/components/InfoHint';
import LoyaltyButton from '~/src/features/payments-abn/components/LoyaltyButton';
import ConnectButton from '~/src/features/payments-abn/components/SeasonTicketPayment/components/ConnectButton';
import LoyaltySection from '~/src/features/payments-abn/components/SeasonTicketPayment/components/LoyaltySection';
import SbpPayment from '~/src/features/payments-abn/components/SeasonTicketPayment/components/SbpPayment';
import { LoadingPaySumSkeleton } from '~/src/features/payments-abn/components/SeasonTicketPayment/components/TicketDetails/LoadingPaySumSkeleton';
import { useSbpHint } from '~/src/features/payments-abn/components/SeasonTicketPayment/hooks/useSbpHint';
import { connectSeasonTicket, updateSeasonTicket } from '~/src/features/payments-abn/components/SeasonTicketPayment/store/actions';
import { selectPaymentSum } from '~/src/features/payments-abn/components/SeasonTicketPayment/store/selectors/selectPaymentSum';
import { useSbpPayment } from '~/src/features/payments-abn/components/useSbpPayment';
import WriteOffDetail from '~/src/features/payments-abn/components/WriteOffDetail';
import { selectBonusText } from '~/src/features/payments-abn/store/selectors/selectBonusText';
import { TState } from '~/src/store';

import { ActionSectionTitle, ActionSectionWrapper } from '~/src/features/payments-abn/styled';
import * as ST from './styled';

const TicketDetails = (): JSX.Element => {
    const dispatch = useDispatch();
    const { loyalty, paymentMethod, seasonTicketPayment, writeOffs, currentPaySum } = useSelector((state: TState) => ({
        loyalty: state.loyalty,
        paymentMethod: state.payment.method?.methodType,
        seasonTicketPayment: state.seasonTicketPayment,
        writeOffs: selectBonusText(state),
        currentPaySum: selectPaymentSum(state),
    }), shallowEqual);

    useSbpPayment();
    useCardPayment();

    const { isInProcess, bonusesInfo: { isUseBonuses, amount }, seasonTicket, paySumIsLoading, paySumIsError } = seasonTicketPayment;

    const { availability: { isRegistered } } = loyalty;

    const { getSbpHint } = useSbpHint();
    const sbpHintData = getSbpHint();

    const onPay = () => {
        dispatch(connectSeasonTicket());
    };

    const onUpdateSeasonTicket = () => {
        if (seasonTicket) {
            dispatch(updateSeasonTicket(seasonTicket));
        }
    };

    return (
        <ActionSectionWrapper>
            <ActionSectionTitle>Детали оплаты</ActionSectionTitle>
            <ST.DetailWrapper>
                {isRegistered
                    ? <LoyaltySection writeOffs={writeOffs}/>
                    : <WriteOffDetail writeOffs={writeOffs}/>
                }

                {paySumIsLoading &&
                    <LoadingPaySumSkeleton/>
                }

                {paySumIsError &&
                    <Alert type={'error'}>
                        <Paragraph4>
                            Что-то пошло не так, не загружается детализация для оплаты. Попробуйте ещё раз. Если не получится, напишите в&nbsp;<OpenChat>поддержку</OpenChat>.
                        </Paragraph4>
                        <Button
                            size={'small'}
                            onClick={onUpdateSeasonTicket}
                        >
                            Перезагрузить
                        </Button>
                    </Alert>
                }

                {(isUseBonuses && amount === seasonTicket?.paySum)
                    ? <LoyaltyButton
                        isLoading={isInProcess}
                        onPayHandler={onPay}
                    />
                    : paymentMethod === 'sbp'
                        ? <>
                            {sbpHintData && <InfoHint {...sbpHintData}/>}
                            <SbpPayment/>
                        </>
                        : <ConnectButton>
                            {`Оплатить ${currentPaySum} ${RubleSymbol}`}
                        </ConnectButton>
                }
            </ST.DetailWrapper>
        </ActionSectionWrapper>
    );
};

export default TicketDetails;
